import { someMatch } from "@configura/web-utilities";
import { CfgFeature } from "./CfgFeature.js";
import { CfgOption } from "./CfgOption.js";
import { CfgProductConfiguration, } from "./CfgProductConfiguration.js";
/**
 * Returns a new array of CfgFeatures that maps to the newFeatureRefs array. Uses CfgFeatures from
 * currentFeatures if they can be found, otherwise makes new.
 */
export function syncCfgFeatures(newFeatureRefs, currentFeatures, rawFeatures, parent, parentConfiguration, parentProduct, rootProduct) {
    const usedRawFeatures = newFeatureRefs
        .map((r) => r.code)
        .map((c) => {
        const rawFeature = rawFeatures.find((f) => c === f.code);
        if (rawFeature === undefined) {
            throw new Error(`Feature not found. Requested feature code: "${c}".`);
        }
        return rawFeature;
    });
    const hasDuplicateDescription = parentProduct.settings.disableMatchOnDescription
        ? true
        : someMatch(usedRawFeatures, (l, r) => {
            return l.description.toLowerCase() === r.description.toLowerCase();
        });
    const newFeatures = [];
    for (const f of usedRawFeatures) {
        const fCode = f.code;
        const fDescription = f.description;
        // It is possible in Cat Creator to add the same Feature twice
        // to the root of a Product. This fills no apparent purpose,
        // and in CET the seem to behave as if they were the same.
        // Removing the duplicates causes sync issues where the server
        // expects all copies, and hence we short circuit them here
        // by letting them refer to the same object instead.
        const twin = newFeatures.find((f) => f.code === fCode);
        if (twin !== undefined) {
            newFeatures.push(twin);
            continue;
        }
        // Description based code helps when switching between
        // products with similar feature-options tree and trying
        // to retain made selections
        const key = fDescription + (fDescription === "" || hasDuplicateDescription ? fCode : "");
        const existingFeature = currentFeatures.find((cfgF) => cfgF.code === fCode);
        if (existingFeature !== undefined) {
            if (hasDuplicateDescription) {
                // HasDuplicateDescription could mean the key need to be more specific compared
                // to the one we already have, so then we set it. An old duplicate description
                // key will cause no harm as it is guaranteed to be at least as specific as this.
                // Not changing the key will make React not rerender, so we don't change.
                existingFeature._internal.key = key;
            }
            newFeatures.push(existingFeature);
            continue;
        }
        newFeatures.push(CfgFeature.make(f, rawFeatures, key, parent, parentConfiguration, parentProduct, rootProduct));
    }
    return newFeatures;
}
export function getMtrlPreview(mtrlApplications) {
    if (mtrlApplications === undefined) {
        return;
    }
    const first = mtrlApplications[0];
    if (first === undefined) {
        return;
    }
    return first.previewUrl;
}
/**
 * Recursively find all additional product references given a product configuration.
 * Only selected options are considered.
 */
export function collectAdditionalProductRefs(parent) {
    function c(parent) {
        const result = [];
        if (parent instanceof CfgOption && parent.selected) {
            result.push(...(parent.rawOption.additionalProductRefs || []));
        }
        if (parent instanceof CfgProductConfiguration || parent.selected) {
            for (const feature of parent.features) {
                for (const option of feature.options) {
                    result.push(...c(option));
                }
            }
        }
        return result;
    }
    return c(parent);
}
